import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import AnnouncementBar from "../components/AnnouncementBar";
import Header from "../components/Header";
import Footer from "../components/Footer";

const PORTRAIT_URL =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_design-portfolio-2079/artifacts/0utyx9dk_1000728201.png";

const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.06 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

const About = () => {
  useReveal();

  return (
    <>
      <AnnouncementBar />
      <Header />

      <section className="about-editorial">
        {/* Portrait column */}
        <div className="about-portrait-col reveal">
          <div
            className="about-portrait-image"
            style={{ backgroundImage: `url(${PORTRAIT_URL})` }}
            role="img"
            aria-label="Binduu Jain photographed in her atelier"
          />
          <div className="about-portrait-caption">
            <div className="about-vlist">
              <span>PEOPLE</span>
              <span>STORIES</span>
              <span>COUTURE</span>
            </div>
            <div className="about-vline" />
            <p className="about-vtag">
              <em>Always<br />the individual<br />at the centre.</em>
            </p>
          </div>
        </div>

        {/* Text column */}
        <div className="about-text-col reveal">
          <p className="about-eyebrow">About</p>
          <h1 className="about-hname">LABEL BINDUU JAIN</h1>
          <div className="about-underline" />

          <h2 className="about-hlede">
            At Label Binduu Jain,<br />couture begins with personality.
          </h2>

          <div className="about-copy">
            <p>
              The journey began in 2021, in a small atelier in Ludhiana,<br />
              where Binduu Jain found an expression for her passion for design.<br />
              What began as a personal creative pursuit has grown into a practice<br />
              in personal couture, with clients across Ludhiana, Los Angeles and Dubai.
            </p>
            <p>
              Binduu creates contemporary, one-of-a-kind pieces that are conceived<br />
              around the woman, not the trend. Every silhouette, fabric and detail is<br />
              considered to reflect her character, sensibility and individual sense of beauty.
            </p>
            <p>
              For Binduu, couture is deeply personal. It is not about recreating what has<br />
              already been seen, but about creating something that could belong to no one else.
            </p>
          </div>

          <p className="about-eyebrow about-eyebrow-alt">Our Philosophy</p>
          <div className="about-underline about-underline-slim" />

          <blockquote className="about-quote-slim">
            <em>
              <span className="quote-mark" aria-hidden="true">&ldquo;</span>
              I believe true luxury is the freedom<br />
              to be entirely yourself.
              <span className="quote-mark quote-mark-close" aria-hidden="true">&rdquo;</span>
            </em>
            <cite>— Binduu Jain</cite>
          </blockquote>

          <div className="about-copy">
            <p>
              This belief defines Binduu&rsquo;s approach to personal couture.<br />
              She sees clothing as an expression of identity — a way for a woman<br />
              to articulate who she is without having to conform to an idea of how she should look.
            </p>
            <p>
              The couture process begins with understanding the woman: her personality,<br />
              her story, her sensibility and the occasion she wants to make her own.<br />
              From that understanding, Binduu translates individuality into silhouette,<br />
              fabric, craftsmanship and detail.<br />
              The result is couture that feels personal, effortless and unmistakably hers.
            </p>
          </div>

          <div className="about-underline about-underline-slim" style={{ margin: "36px 0 22px" }} />

          <div className="about-triplet-slim">
            <p><em>Your personality is the starting point.</em></p>
            <p><em>Your story is the inspiration.</em></p>
            <p><em>The garment is the expression.</em></p>
          </div>

          <div style={{ marginTop: 48 }}>
            <Link to="/pages/waitlist-registration" className="btn-primary">
              Register Your Interest
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default About;
